"use client";

import type React from "react";

import { DynamicLinkButton } from "@/components/common/dynamic-link-button/dynamic-link-button";
import {
  IProjectShowcaseTabs,
  Project_Showcase_Tabs_Content,
} from "@/lib/dto/tech-stack/project-showcase-tabs/project-showcase-tabs-data";
import Image from "next/image";
import { useRef, useState } from "react";

interface Tab {
  id: string;
  title: string;
  content: React.ReactNode;
}

const TabSwitcher = () => {
  const [activeTab, setActiveTab] = useState("mobile-apps");

  const renderTabContent = (tab: IProjectShowcaseTabs) => (
    <div className="flex flex-col-reverse lg:ml-10 gap-y-6 md:gap-y-3 lg:gap-y-0 lg:grid lg:grid-cols-2 lg:gap-x-12 items-center w-full">
      <div className="space-y-6 lg:mr-[-20px] flex flex-col items-center lg:items-start">
        <h2 className="text-center lg:text-left text-[22px] md:text-2xl xl:text-[28px] 2xl:text-4xl font-semibold text-white xl:max-w-2/2">
          {tab.heading}
        </h2>
        {tab.list_item.map((v, i) => (
          <div key={i} className="space-y-4 lg:max-w-120 w-full flex justify-center lg:justify-start">
            <div className="flex items-start gap-3">
              <div className="flex items-start max-w-[80%] mt-1 lg:w-auto lg:max-w-none justify-center gap-x-2 mx-auto lg:mx-0">
                <Image src={v.icon_src} alt={v.icon_alt} className="w-6 object-contain" />
                <p className="text-white text-base text-center lg:text-left">{v.text}</p>
              </div>
            </div>
          </div>
        ))}

        <DynamicLinkButton
          value={tab.buttonLabel}
          href={tab.buttonLink || "#"}
          variant="secondary"
          className="mt-4"
        />
      </div>

      <div className="flex justify-center items-center lg:justify-end">
        <div className="flex justify-center w-full lg:max-w-3xl">
          <Image
            src={tab.image.src}
            alt={tab.image.alt}
            width={tab.image.width}
            height={tab.image.height}
            className="w-3/4 md:w-2/3 lg:w-full h-auto"
            priority
          />
        </div>
      </div>
    </div>
  );

  const tabs: Tab[] = Project_Showcase_Tabs_Content.map((tab) => ({
    id: tab.id,
    title: tab.title,
    content: renderTabContent(tab),
  }));

  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
    const tabEl = tabRefs.current[tabId];
    if (tabEl) {
      tabEl.scrollIntoView({
        behavior: "smooth",
        inline: "center",
        block: "nearest",
      });
    }
  };

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="pt-6 md:pt-12 lg:pt-16.5 w-full">
      <div className="mx-auto w-full">
        {/* Tab Navigation */}
        <div className="flex justify-center mb-12 lg:mb-16">
          <div className="relative w-full max-w-[95vw] md:max-w-[67vw] lg:max-w-max">
            <div className="bg-white/30 shadow-[0px_24px_48px_rgba(21,26,40,0.5)] backdrop-blur-md rounded-lg md:rounded-2xl p-2 md:p-3 flex flex-row gap-2 overflow-x-auto no-scrollbar">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  ref={(el) => {
                    tabRefs.current[tab.id] = el;
                  }}
                  onClick={() => handleTabClick(tab.id)}
                  className={`relative px-3 md:px-4 py-2 md:py-3 rounded-lg md:rounded-xl text-sm lg:text-base font-medium transition-all duration-300 whitespace-nowrap ${activeTab === tab.id
                    ? "bg-white text-custom-blue shadow-lg font-semibold"
                    : "text-white/80 hover:text-white hover:bg-white/10 font-light"
                    }`}
                >
                  {tab.title}
                  {activeTab === tab.id && (
                    <div
                      className="absolute inset-0 bg-white rounded-xl shadow-lg"
                      style={{ zIndex: -1 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="relative w-full">
          <div className="w-full">
            {activeTabContent}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TabSwitcher;
